import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, OnInit, ViewEncapsulation} from '@angular/core';
import {select, Store} from '@ngrx/store';
import {IgenomicsState} from './core/interfaces/genomics-state.interface';
import {Observable} from 'rxjs/Observable';
import {CoreActionCreatorService} from './core/services/core-action-creator.service';
import {DynamicModalLoaderService} from './layout/services/dynamic-modal-loader.service';
import {ConfirmModalComponent} from './layout/components/modals/confirm-modal/confirm-modal.component';
import {ConfirmModalLoaderPayload} from './layout/classes/confirm-modal-loader-payload';
import { distinctUntilChanged, filter, flatMap, take, tap} from 'rxjs/operators';
import {ModalTypes} from './layout/components/modals/contants/modal-types.enum';
import {IGeneralErrorState} from '@ispm/core/core.reducer';
import {getNotification} from '@ispm/dashboard/notification/store/notification.selectors';
import {INotificationState} from '@ispm/dashboard/notification/store/notification.reducer';
import {INotificationConfiguration} from '@ispm/dashboard/notification/class/notification.defs';
import {BrowserCacheService} from '@ispm/core/services/browser-cache.service';
import {UserActionCreatorService} from '@ispm/user/services/user-action-creator.service';
import {TranslationHandlerService} from '@ispm/core/services/translation-handler.service';
import {TranslateService} from '@ngx-translate/core';
import {URLsForSSO} from '@ispm/shared/constants/urls-constants';
import {RouterManagerService} from '@ispm/core/services/router-manager.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css', '../genomics-ngjs/styles/helpers/_label_tags.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class AppComponent implements OnInit {

  public LOADER_TEXT: string;
  public loader$: Observable<boolean>;
  public navigationEndIsTriggered: boolean = true;
  public notificationConfig: Observable<INotificationConfiguration>;
  public notificationStore: Store<INotificationState>;

  private _callBack: any;
  private organizationConfig: Observable<any>;

  constructor(
    private store: Store<IgenomicsState>,
    private _coreAC: CoreActionCreatorService,
    private browserCacheService: BrowserCacheService,
    private userActionCreatorService: UserActionCreatorService, // don't remove this service because @Injectable() is not root
    private coreActionCreatorService: CoreActionCreatorService,
    private translationService: TranslationHandlerService, // don't remove this service because @Injectable() is not root
    private _translator: TranslateService,
    private injector: Injector,
    private cdr: ChangeDetectorRef,
    private routerManagerService: RouterManagerService
  ) {
    this.loader$ = this.store.select('core', 'loader').pipe(
      distinctUntilChanged(),
      tap(() => this.cdr.markForCheck()),
    );

    this.notificationStore = this.store.select('notification') as any;
    this.organizationConfig = this.store.select('users', 'organizationConfiguration');
    this.LOADER_TEXT = (<any>window).prefetchedTranslationData.LOADER_TEXT;
    this.routerManagerService.$currentState.pipe(filter(currUrl => !!currUrl)).subscribe((currUrl) => {
    this.navigationEndIsTriggered = URLsForSSO.has(currUrl);
    });
  }

  ngOnInit(): void {
    this._translator.setDefaultLang('en-US');
    this.browserCacheService.generateCacheId();
    this.initInjectorListener();
    this.notificationConfig = this.notificationStore.pipe(select(getNotification)).pipe(filter(notification => !!notification));
    this.organizationConfig.pipe(
      filter(data => !!data),
      tap(data => {
        if (data.hideTopPanelIfInFrame) {
          this._coreAC.setIsEmbedded(this.isInIframe());
        }
      })
    ).subscribe();

    // if user refreshes the browser need to start to calculate user interaction after the refresh has finished
    this.coreActionCreatorService.skipUserInteractionUpdate(false);
  }

  initInjectorListener() {
    this._coreAC.getUpgradeInjectorStatus().pipe(
      filter(status => !!status),
      take(1)
    ).subscribe(() => {
      this.initErrorListener();
    });
  }

  initErrorListener() {
    const _routeHandler: RouterManagerService = this.injector.get(RouterManagerService);
    const _modalLoader = this.injector.get(DynamicModalLoaderService);
    this._coreAC.getErrorState().pipe(
      filter((errorState: IGeneralErrorState) => errorState.status),
      flatMap((errorState: IGeneralErrorState) => {
        this._callBack = errorState.callback;
        return _modalLoader.popupModal(ConfirmModalComponent, new ConfirmModalLoaderPayload(
            errorState.title,
            errorState.content,
            errorState.btntxt
          ).setBackdropAllowed(false),
          ModalTypes.ERROR
        );
      })
    ).subscribe(response => {
      this._coreAC.setErrorModal(false);
      if (this._callBack === null) {
        _routeHandler.reload()
          .then(() => {
            this.initInjectorListener();
          });
      } else {
        this._callBack();
      }
    });
  }

  private isInIframe() {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }
}

